import React, { ReactElement, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, Tooltip } from '@mui/material';
import { DefaultValues, FieldValues, FormProvider, useForm } from 'react-hook-form';
import { DialogClose, useAlerts } from '@qualifio/ui-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RuleAttributes, RuleInput, RuleType } from '@loyalty/shared-common/rule-api-client';
import { Schema } from 'joi';
import { extractErrorMessage } from '@loyalty/shared-common';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { RuleConflictModal, RuleConflictModalProps } from './RuleConflictModal';
import { useApi } from '../../shared/api-client/ApiClientProvider';
import { useJoiResolver } from '../../shared/forms/use-joi-resolver';
import { useProgram } from '../../loyalty/ProgramContext';
import { useRules } from '../RulesContext';
import { useSelectiveEffect } from '../../shared/use-selective-effect';

interface Props<T> {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  rule?: RuleAttributes;
  formSchema: Schema<T>;
  getResetValue: () => DefaultValues<T>;
  formValueToRuleInput: (formValue: T) => RuleInput;
  children: ReactElement;
  canEdit: boolean;
  ruleType: RuleType;
}

export function RuleModal<T extends FieldValues>({
  isOpen,
  setIsOpen,
  rule,
  formSchema,
  getResetValue,
  formValueToRuleInput,
  children,
  canEdit,
  ruleType,
}: Props<T>) {
  const { t } = useTranslation();
  const { programId } = useProgram();
  const { fetchRules } = useRules();
  const [conflictModalIsOpen, setConflictModalIsOpen] = useState(false);
  const [conflictDialogProps, setConflictDialogProps] = useState<
    Omit<RuleConflictModalProps, 'isOpen' | 'setIsOpen'>
  >({
    ruleType,
  });
  const onClose = () => setIsOpen(false);

  const form = useForm<T>({
    mode: 'onChange',
    resolver: useJoiResolver(formSchema),
  });

  const {
    reset,
    handleSubmit,
    formState: { isDirty },
  } = form;

  useSelectiveEffect(
    () => {
      reset(getResetValue());
    },
    [getResetValue, reset, rule],
    [rule],
  );

  const { ruleApi } = useApi();
  const { notify } = useAlerts();
  const { isPending: isCreating, mutate: createRule } = useMutation({
    mutationKey: ['rule.create', programId],

    mutationFn: (data: RuleInput) =>
      ruleApi.addRule(programId, data).then(() => {
        fetchRules(ruleType);
        onClose();
        reset(getResetValue());
      }),

    onError: (err: any) => notify(extractErrorMessage(err) || t('error.page'), 'error'),
  });
  const { isPending: isUpdating, mutate: updateRule } = useMutation({
    mutationKey: ['rule', rule?.id, 'update'],

    mutationFn: (data: RuleInput) => {
      if (rule) {
        return ruleApi
          .updateRule(rule.id, data)
          .then(() => {
            fetchRules(ruleType);
            onClose();
            reset(getResetValue());
          })
          .catch(error => {
            if (error.response?.status === 409) {
              setConflictDialogProps({
                ...conflictDialogProps,
                lastModification: error.response?.data?.details,
              });
              setConflictModalIsOpen(true);
            } else {
              throw error;
            }
          });
      } else {
        throw new Error('No rule selected');
      }
    },

    onError: (err: any) => notify(extractErrorMessage(err) || t('error.page'), 'error'),
  });

  const isLoading = isCreating || isUpdating;
  const isEdit = rule && rule.id;

  const onSubmit = (value: T) => {
    const data = formValueToRuleInput(value as T);
    if (isEdit) {
      updateRule(data);
    } else {
      createRule(data);
    }
  };

  return (
    <>
      <RuleConflictModal
        {...conflictDialogProps}
        setIsOpen={setConflictModalIsOpen}
        isOpen={conflictModalIsOpen}
      />
      <Dialog
        open={isOpen}
        onClose={onClose}
        PaperProps={{
          sx: {
            position: 'fixed',
            top: 25,
            left: 25,
            right: 25,
            bottom: 25,
            m: 0,
            maxWidth: 'none',
            maxHeight: 'none',
          },
        }}
      >
        <FormProvider<T> {...form}>
          <form
            onSubmit={handleSubmit(onSubmit, console.warn)}
            style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <DialogClose close={onClose} />
            <DialogContent
              sx={{ textAlign: 'start !important', display: 'flex', flexGrow: '1', width: '100%' }}
            >
              <Box sx={{ width: '100%' }}>{children}</Box>
            </DialogContent>
            <DialogActions
              sx={{
                display: 'flex',
                justifyContent: 'space-between !important',
                paddingLeft: 4,
                paddingRight: 4,
              }}
            >
              <Button color="grey" onClick={onClose} disabled={isLoading}>
                {t('generic.actions.cancel')}
              </Button>
              <Tooltip
                title={canEdit ? '' : t('shared.action.permission.required')}
                placement="left"
              >
                <div>
                  <Button
                    startIcon={
                      <FontAwesomeIcon
                        fontSize="inherit"
                        icon={isLoading ? faSpinner : faCheck}
                        spin={isLoading}
                      />
                    }
                    color="success"
                    type="submit"
                    disabled={!isDirty || isLoading || !canEdit}
                  >
                    {t('shared.actions.save')}
                  </Button>
                </div>
              </Tooltip>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
    </>
  );
}
